// Schema-aware table for `audit-timeline-v1` registry files (Timeline
// Lane 1.0.0). Renders registry/audit-timeline-events.yaml —
// dr.the0's modern-precision workspace audit log, consumed at runtime
// by the standalone D3 timeline viz (audit variant) at
// code-tron/timeline-v010-precise/. Read-only; do not reshape events[].
//
// Columns: ID (mono, small), Name (bold), When (second-precision UTC,
// sortable chronological — always CE, no BCE handling), Track (verbose
// label + chip keyed off cat: sci/tech/exploration/eng), Location
// (mono — it's a filesystem path), Description (truncated + full on
// hover; multi-line ok), Tags (chips, same shape as Bookmarks), Source.

import { useMemo, useState } from 'react';
import yaml from 'js-yaml';
import { validateRegistryEntry } from '@captain-tasko/shared';
import { WarningRow } from '../WarningRow.js';
import { RegistryTableHeader } from '../RegistryTableHeader.js';
import { SortableTh } from '../SortableTh.js';
import { useTableSort } from '../../lib/sort.js';
import { SourceCell, TrackChip } from './EventsTable.js';
import { coerceDateString, formatAuditTimestamp, parseAuditMillis } from './formatDate.js';

// ── Schema, mirrors registry/audit-timeline-events.yaml ─────────────
interface AuditEvent {
  /** Required for this schema — audit-2026-06-04-kickoff-shaped. */
  id: string;
  name: string;
  /** ISO 8601, second precision, always CE (`Z` or numeric offset). */
  date: string;
  /** 4-track workspace vocabulary. */
  track: string;
  /** sci | tech | exploration | eng */
  cat?: string;
  /** Filesystem path being acted on. */
  location?: string;
  desc?: string;
  sourceLabel?: string;
  sourceUrl?: string;
  tags?: string[];
}

interface AuditFile {
  schema_version?: number;
  events?: AuditEvent[];
  [k: string]: unknown;
}

// ── Component ───────────────────────────────────────────────────────

// Sort accessors (CTSK-267) — module-level so the useTableSort memo
// key is referentially stable. When sorts numerically by epoch millis.
const AUDIT_SORT_ACCESSORS = {
  id:       (e: AuditEvent) => e.id,
  name:     (e: AuditEvent) => e.name,
  date:     (e: AuditEvent) => parseAuditMillis(e.date),
  track:    (e: AuditEvent) => e.track,
  location: (e: AuditEvent) => e.location ?? null,
  desc:     (e: AuditEvent) => e.desc ?? null,
  tags:     (e: AuditEvent) => (e.tags && e.tags.length > 0 ? e.tags.join(', ') : null),
  source:   (e: AuditEvent) => e.sourceLabel ?? e.sourceUrl ?? null,
};

export function AuditTimelineTable({ yamlText }: { yamlText: string }) {
  const { entries, parseError } = useMemo<{
    entries: AuditEvent[];
    parseError: string | null;
  }>(() => {
    if (!yamlText) return { entries: [], parseError: null };
    try {
      const parsed = yaml.load(yamlText) as AuditFile | null;
      const list = ((parsed?.events ?? []) as AuditEvent[]).map(normalizeAuditEvent);
      return { entries: list, parseError: null };
    } catch (err) {
      return { entries: [], parseError: (err as Error).message };
    }
  }, [yamlText]);

  // ── CTSK-267: search / filter / sort ──────────────────────────────
  const [search, setSearch] = useState('');
  const [filterTrack, setFilterTrack] = useState('');
  // Second-precision date range (datetime-local, step=1). Always CE —
  // no BCE handling in this schema.
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const trackOptions = useMemo(
    () => [...new Set(entries.map((e) => e.track).filter(Boolean))].sort(),
    [entries],
  );

  const filtered = useMemo(() => {
    let rows = entries;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((e) => {
        const hay = [e.id, e.name, coerceDateString(e.date), e.track, e.cat ?? '', e.location ?? '', e.desc ?? '', ...(e.tags ?? []), e.sourceLabel ?? '']
          .join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    if (filterTrack) rows = rows.filter((e) => e.track === filterTrack);
    const tFrom = dateFrom ? new Date(dateFrom).getTime() : null;
    const tTo = dateTo ? new Date(dateTo).getTime() : null;
    if (tFrom != null || tTo != null) {
      rows = rows.filter((e) => {
        const t = parseAuditMillis(e.date);
        if (t == null) return false;
        if (tFrom != null && !Number.isNaN(tFrom) && t < tFrom) return false;
        if (tTo != null && !Number.isNaN(tTo) && t > tTo) return false;
        return true;
      });
    }
    return rows;
  }, [entries, search, filterTrack, dateFrom, dateTo]);

  // Tri-state sortable columns; default stays chronological asc — the
  // pre-CTSK-267 behavior of this table.
  const { sorted, sort, setSort } = useTableSort(filtered, AUDIT_SORT_ACCESSORS, { key: 'date', dir: 'asc' });

  const reset = () => {
    setSearch('');
    setFilterTrack('');
    setDateFrom('');
    setDateTo('');
    setSort(null);
  };

  return (
    <div>
      {parseError && (
        <div role="alert" style={{ padding: 12, background: 'color-mix(in srgb, var(--danger-primary, #B3261E) 12%, var(--surface-raised))', border: '1px solid var(--danger-primary, #B3261E)', borderRadius: 'var(--radius-md)', color: 'var(--danger-primary, #B3261E)', fontSize: 13, marginBottom: 16 }}>
          <strong>YAML parse error:</strong> {parseError}
        </div>
      )}

      {entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
          No audit-timeline entries — add under <code style={{ fontFamily: 'var(--font-mono)' }}>events:</code> in{' '}
          <code style={{ fontFamily: 'var(--font-mono)' }}>captain-tasko/registry/audit-timeline-events.yaml</code>.
        </div>
      ) : (
        <>
          <RegistryTableHeader
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search id, name, track, location, description, tags…"
            filters={[
              { label: 'All tracks', value: filterTrack, options: trackOptions, onChange: setFilterTrack },
            ]}
            onReset={reset}
          >
            <input
              type="datetime-local"
              step={1}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              aria-label="From date"
              title="Earliest timestamp (second precision)"
              style={dateInputStyle}
            />
            <input
              type="datetime-local"
              step={1}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              aria-label="To date"
              title="Latest timestamp (second precision)"
              style={dateInputStyle}
            />
          </RegistryTableHeader>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            {sorted.length === entries.length
              ? `${entries.length} audit events`
              : `Showing ${sorted.length} of ${entries.length} audit events`}
          </div>
          {sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
              No audit events match.
            </div>
          ) : (
          <table className="grid" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 200 }} />
              <col style={{ width: 200 }} />
              <col style={{ width: 165 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 200 }} />
              <col />
              <col style={{ width: 150 }} />
              <col style={{ width: 170 }} />
            </colgroup>
            <thead>
              <tr>
                <SortableTh sortKey="id" sort={sort} onSortChange={setSort}>ID</SortableTh>
                <SortableTh sortKey="name" sort={sort} onSortChange={setSort}>Name</SortableTh>
                <SortableTh sortKey="date" sort={sort} onSortChange={setSort}>When</SortableTh>
                <SortableTh sortKey="track" sort={sort} onSortChange={setSort}>Track</SortableTh>
                <SortableTh sortKey="location" sort={sort} onSortChange={setSort}>Location</SortableTh>
                <SortableTh sortKey="desc" sort={sort} onSortChange={setSort}>Description</SortableTh>
                <SortableTh sortKey="tags" sort={sort} onSortChange={setSort}>Tags</SortableTh>
                <SortableTh sortKey="source" sort={sort} onSortChange={setSort}>Source</SortableTh>
              </tr>
            </thead>
            <tbody>
              {sorted.map((e, idx) => {
                // CTSK-264 — read-time validation (same dispatcher as
                // the CTSK-255 write path, on the normalized entry).
                const validation = validateRegistryEntry('audit-timeline-v1', e);
                const cells = (
                  <>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.id}>
                    {e.id}
                  </td>
                  <td style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.name}>
                    {e.name}
                  </td>
                  <td style={{ fontSize: 12, whiteSpace: 'nowrap' }} title={coerceDateString(e.date)}>
                    {formatAuditTimestamp(e.date)}
                  </td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.track}>
                    <TrackChip track={e.track} cat={e.cat} />
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.location ?? ''}>
                    {e.location ?? <span style={{ color: 'var(--text-muted)', fontFamily: 'inherit' }}>—</span>}
                  </td>
                  <td style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.desc ?? ''}>
                    {e.desc ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td>
                    {e.tags && e.tags.length > 0 ? (
                      <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                        {e.tags.map((t) => (
                          <span
                            key={t}
                            style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, background: 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))', color: 'var(--accent-primary)' }}
                          >
                            {t}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <SourceCell sourceLabel={e.sourceLabel} sourceUrl={e.sourceUrl} />
                  </td>
                  </>
                );
                const key = e.id ?? `row-${idx}`;
                return validation.ok
                  ? <tr key={key}>{cells}</tr>
                  : <WarningRow key={key} issues={validation.errors}>{cells}</WarningRow>;
              })}
            </tbody>
          </table>
          )}
        </>
      )}
    </div>
  );
}

const dateInputStyle: React.CSSProperties = {
  padding: '5px 8px',
  fontSize: 12,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  background: 'var(--surface-raised)',
  color: 'var(--text-primary)',
};

// ── Helpers ─────────────────────────────────────────────────────────

// ISO timestamps may resolve to JS Date objects depending on quoting;
// coerce to strings. `>`-folded desc blocks carry a trailing newline.
function normalizeAuditEvent(e: AuditEvent): AuditEvent {
  const out = { ...e };
  out.date = coerceDateString(out.date as unknown);
  if (typeof out.desc === 'string') out.desc = out.desc.trim();
  return out;
}
